import React, { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import LateFeeStatItem from 'components/pricing/LateFees/LateFeesListing/LateFeeStatItem'
import T from 'T'
import { capitalize, get } from 'lodash'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LateInvoiceItem from 'components/customer-details/content/fees/late_fees/LateInvoiceItem'
import useFormatLateFee from 'components/customer-details/content/fees/late_fees/useFormatLateFee'
import { useLazyFetchLateFeeInvoicesByLateFeeIdAndAccountQuery } from 'api/pricing/lateFeesCrud'
import { HHAlert } from 'components/common/HHAlert'
import PropTypes from 'prop-types'
import { START_OFFSET_UNITS } from 'components/pricing/LateFees/CreateEditLateFeesDialog/settings'
import InvoiceAccordionSummarySkeleton from 'components/customer-details/content/invoices/InvoiceAccordionSummarySkeleton'
import LateFeesCustomerActionMenu from 'components/customer-details/content/fees/late_fees/LateFeesCustomerActionMenu'
import RenderLateFeeAmount from 'components/pricing/LateFees/common/RenderLateFeeAmount'

const LateFeesCustomerAccordionItem = lateFee => {
  const {
    id,
    feeName = 'Late fee',
    duration = 0,
    pricingPeriod = 'Daily',
    startOffset = 0,
    feeRate,
    amountCents = 0,
    amountPercent = 0,
    businessLine = '',
    accountCount = 0,
    accountId = '',
    startOffsetUnit = START_OFFSET_UNITS.DAY,
  } = lateFee
  const theme = useTheme()
  const capitalizedPricingPeriod = useMemo(() => capitalize(pricingPeriod), [pricingPeriod])
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const isTablet = useMediaQuery(theme.breakpoints.only('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const { formattedStartDate, formattedDuration, isPricingPeriodOnce } = useFormatLateFee({
    feeName,
    duration,
    pricingPeriod,
    startOffset,
    feeRate,
    amountCents,
    amountPercent,
    businessLine,
    accountCount,
    startOffsetUnit,
  })
  const [fetchInvoicesLinkedToLateFee, { isLoading, isFetching, data, isSuccess }] = useLazyFetchLateFeeInvoicesByLateFeeIdAndAccountQuery()
  const invoices = get(data, 'invoices', [])
  const hasInvoices = invoices.length > 0 && isSuccess
  const loading = useMemo(() => isLoading || isFetching, [isLoading, isFetching])
  const [isExpanded, setIsExpanded] = useState(false)
  const toggleIsExpanded = () => setIsExpanded(!isExpanded)

  useEffect(() => {
    fetchInvoicesLinkedToLateFee({ accountId, id }).unwrap()
  }, [])

  return (
    <Accordion
      elevation={0}
      expanded={isExpanded}
      sx={{
        '&.Mui-expanded': {
          m: 0,
        },
        '& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content': {
          mb: 0,
          mt: 0,
        },
        '&:before': {
          display: 'none',
        },
      }}
      disablePointer
      expandIcon={undefined}
      iconGap={false}
    >
      <AccordionSummary
        sx={{
          p: 0,
        }}
      >
        <ListItem secondaryAction={<LateFeesCustomerActionMenu lateFee={lateFee} />}>
          <ListItemIcon
            sx={{
              minWidth: 0,
            }}
          >
            <IconButton onClick={toggleIsExpanded}>{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}</IconButton>
          </ListItemIcon>
          <Box height="100%" width="100%">
            <Grid container alignItems="center" justifyContent="center" flexWrap="nowrap">
              <Grid
                size={{
                  xs: 'grow',
                  sm: 'grow',
                  md: 'grow',
                  lg: 'grow',
                }}
              >
                <ListItem disableGutters>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography variant="h4" color="textPrimary" fontWeight={600} noWrap>
                        {feeName}
                      </Typography>
                    }
                    secondary={
                      <Grid flexWrap={isTabletOrMobile ? 'wrap' : 'nowrap'} justifyContent="flex-start" alignItems="center" container>
                        {isDesktop && (
                          <>
                            <LateFeeStatItem
                              primary={formattedStartDate}
                              secondary={capitalize(T.GRACE_PERIOD)}
                              divider
                              lg={2}
                              xl={1}
                              ListItemSx={{
                                pl: 0,
                              }}
                              sx={{
                                minWidth: {
                                  lg: '100px',
                                  xl: '150px',
                                },
                              }}
                            />
                            {!isPricingPeriodOnce && (
                              <LateFeeStatItem
                                primary={formattedDuration}
                                secondary={capitalize(T.DURATION)}
                                divider
                                lg={2}
                                xl={1}
                                ListItemSx={{
                                  pl: 1,
                                }}
                                sx={{
                                  minWidth: {
                                    lg: '100px',
                                    xl: '150px',
                                  },
                                }}
                              />
                            )}
                            <LateFeeStatItem
                              primary={capitalizedPricingPeriod}
                              secondary={capitalize(T.PRICING_PERIOD)}
                              divider={isXs || isDesktop}
                              lg={2}
                              xl={1}
                              ListItemSx={{
                                pl: 1,
                              }}
                              sx={{
                                minWidth: {
                                  lg: '100px',
                                  xl: '150px',
                                },
                              }}
                            />
                            <LateFeeStatItem
                              lg={2}
                              xl={1}
                              sx={{
                                minWidth: {
                                  lg: '100px',
                                  xl: '150px',
                                },
                              }}
                              ListItemSx={{
                                pl: 1,
                              }}
                              primary={accountCount}
                              secondary="accounts"
                              divider
                            />
                            <LateFeeStatItem
                              lg={4}
                              primary={businessLine || 'None'}
                              secondary={capitalize(T.BUSINESS_LINE)}
                              xl={8}
                              ListItemSx={{
                                pl: 1,
                              }}
                              sx={{
                                maxWidth: {
                                  lg: 'unset',
                                  xl: 'unset',
                                },
                              }}
                            />
                          </>
                        )}
                        {isTablet && (
                          <>
                            <Grid container size={12}>
                              <LateFeeStatItem sm md primary={formattedStartDate} secondary={capitalize(T.GRACE_PERIOD)} divider />
                              <LateFeeStatItem sm md primary={formattedDuration} secondary={capitalize(T.DURATION)} divider />
                              <LateFeeStatItem sm md primary={capitalizedPricingPeriod} secondary={capitalize(T.PRICING_PERIOD)} />
                            </Grid>
                            <Grid container flexWrap="nowrap" size={12}>
                              <LateFeeStatItem sm={4} md={4} primary={accountCount} secondary={T.ACCOUNTS} divider />
                              <LateFeeStatItem
                                md
                                primary={businessLine || 'None'}
                                secondary={capitalize(T.BUSINESS_LINE)}
                                sm
                                sx={{
                                  maxWidth: {
                                    md: 'unset',
                                  },
                                }}
                              />
                            </Grid>
                          </>
                        )}
                        {isMobile && (
                          <>
                            <Grid container size={12}>
                              <LateFeeStatItem xs={12} sm md primary={formattedStartDate} secondary={capitalize(T.GRACE_PERIOD)} divider />
                              <LateFeeStatItem
                                divider={isXs}
                                md
                                primary={formattedDuration}
                                secondary={capitalize(T.DURATION)}
                                sm
                                xs={12}
                              />
                            </Grid>
                            <Grid container size={12}>
                              <LateFeeStatItem
                                divider
                                md
                                primary={capitalizedPricingPeriod}
                                secondary={capitalize(T.PRICING_PERIOD)}
                                sm
                                xs={12}
                              />
                              <LateFeeStatItem xs={12} sm md primary={accountCount} secondary={T.ACCOUNTS} divider={isXs} />
                            </Grid>
                            <LateFeeStatItem
                              primary={businessLine || 'None'}
                              secondary={capitalize(T.BUSINESS_LINE)}
                              xs={12}
                              sx={{
                                maxWidth: {
                                  sm: 'unset',
                                },
                              }}
                            />
                          </>
                        )}
                      </Grid>
                    }
                  />
                </ListItem>
              </Grid>
              <Grid
                sx={{
                  minWidth: {
                    xs: '12rem',
                    sm: '12rem',
                    md: '12rem',
                    lg: '13rem',
                    xl: '24rem',
                  },
                }}
              >
                <Grid container flexWrap="nowrap" alignItems="center" justifyContent="flex-end" columnGap={2}>
                  <Grid>
                    <Typography variant="h4" color="textPrimary" fontWeight={600} noWrap>
                      <RenderLateFeeAmount feeRate={feeRate} amountCents={amountCents} amountPercent={amountPercent} />
                    </Typography>
                  </Grid>
                  <Grid />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </ListItem>
      </AccordionSummary>
      <Divider />
      <AccordionDetails
        sx={{
          p: 0,
        }}
      >
        <Box
          width="100%"
          sx={{
            ...(!hasInvoices && {
              p: 2,
            }),
          }}
        >
          {!hasInvoices && (
            <HHAlert
              severity="info"
              borderColor={theme.palette.info.main}
              sx={{
                justifyContent: 'center',
                p: 2,
              }}
            >
              No late fees on this account yet
            </HHAlert>
          )}
          {loading && <InvoiceAccordionSummarySkeleton />}
          {invoices.map(invoice => {
            return (
              <>
                <LateInvoiceItem {...invoice} accountId={accountId} />
                <Divider />
              </>
            )
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

LateFeesCustomerAccordionItem.propTypes = {
  lateFee: PropTypes.object,
}

export default LateFeesCustomerAccordionItem
